import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, Pagination, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { ExpandMore, Download, Refresh } from '@mui/icons-material';

const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    level: '',
    category: '',
    userId: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  useEffect(() => {
    fetchLogs();
    fetchAnalytics();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`https://salon.rockyroadsolutions.com/api/logs?${params}`);
      const data = await response.json();
      
      setLogs(data.logs);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
    setLoading(false);
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`https://salon.rockyroadsolutions.com/api/logs/analytics?${params}`);
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR': return 'error';
      case 'WARN': return 'warning';
      case 'INFO': return 'info';
      case 'DEBUG': return 'default';
      default: return 'default';
    }
  };

  const exportLogs = async () => {
    const params = new URLSearchParams({
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      limit: '10000'
    });

    const response = await fetch(`https://salon.rockyroadsolutions.com/api/logs?${params}`);
    const data = await response.json();
    
    const csv = [
      'Timestamp,Level,Category,Message,User ID,IP Address',
      ...data.logs.map((log: any) => 
        `${log.created_at},${log.level},${log.category},"${log.message}",${log.user_id || ''},${log.ip_address || ''}`
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">System Logs & Analytics</Typography>
        <Box display="flex" gap={2}>
          <Button startIcon={<Refresh />} onClick={() => { fetchLogs(); fetchAnalytics(); }}>
            Refresh
          </Button>
          <Button startIcon={<Download />} onClick={exportLogs}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Analytics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Log Levels</Typography>
              {analytics.levelStats?.map((stat: any) => (
                <Box key={stat.level} display="flex" justifyContent="space-between">
                  <Chip label={stat.level} color={getLogLevelColor(stat.level) as any} size="small" />
                  <Typography>{stat.count}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Categories</Typography>
              {analytics.categoryStats?.slice(0, 5).map((stat: any) => (
                <Box key={stat.category} display="flex" justifyContent="space-between">
                  <Typography variant="body2">{stat.category}</Typography>
                  <Typography>{stat.count}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Error Trends</Typography>
              {analytics.errorTrends?.slice(-7).map((trend: any) => (
                <Box key={trend.date} display="flex" justifyContent="space-between">
                  <Typography variant="body2">{new Date(trend.date).toLocaleDateString()}</Typography>
                  <Typography color="error">{trend.count} errors</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Level"
              value={filters.level}
              onChange={(e) => setFilters({...filters, level: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="ERROR">Error</MenuItem>
              <MenuItem value="WARN">Warning</MenuItem>
              <MenuItem value="INFO">Info</MenuItem>
              <MenuItem value="DEBUG">Debug</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="AUTH">Authentication</MenuItem>
              <MenuItem value="APPOINTMENT">Appointments</MenuItem>
              <MenuItem value="PAYMENT">Payments</MenuItem>
              <MenuItem value="CLIENT">Clients</MenuItem>
              <MenuItem value="STAFF">Staff</MenuItem>
              <MenuItem value="SYSTEM">System</MenuItem>
              <MenuItem value="API">API</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="User ID"
              value={filters.userId}
              onChange={(e) => setFilters({...filters, userId: e.target.value})}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search Message"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              size="small"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setFilters({ level: '', category: '', userId: '', startDate: '', endDate: '', search: '' })}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>User</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip label={log.level} color={getLogLevelColor(log.level) as any} size="small" />
                </TableCell>
                <TableCell>{log.category}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.message}
                </TableCell>
                <TableCell>{log.user_id || '-'}</TableCell>
                <TableCell>{log.ip_address || '-'}</TableCell>
                <TableCell>
                  {log.metadata && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="caption">View Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                          {JSON.stringify(log.metadata, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination 
          count={totalPages} 
          page={page} 
          onChange={(_, value) => setPage(value)} 
        />
      </Box>
    </Box>
  );
};

export default SystemLogs;
